import React from "react";

import {
  getCurrentRoundTarget,
  getGoalBatchesCompleted,
  getGoalRoundDisplayBatchSize,
  getTotalCounted,
} from "../../utils/counterState";

import resetIcon from "../../Images/reset-icon.svg";
import minusIcon from "../../Images/minus-icon.svg";
import plusIcon from "../../Images/plus-icon.svg";
import listIcon from "../../Images/list-icon.svg";
import dataIcon from "../../Images/data-icon.svg";

export default function CounterFooter({
  activeCounter,
  onResetCount,
  onDecrement,
  onShowCreateCounter,
  onShowLists,
  onOpenSettings,
  onTapFeedback,
}) {
  // -------------------------
  // Derived values
  // -------------------------
  const isGoal = activeCounter?.type === "goal";

  const rounds = isGoal
    ? getGoalBatchesCompleted(activeCounter)
    : activeCounter?.rounds ?? 0;

  const current = activeCounter?.currentCount ?? 0;
  const perDay = activeCounter?.perDayCount ?? 0;
  const limit = activeCounter?.limit ?? 0;

  const totalSoFar = isGoal
    ? getTotalCounted(activeCounter)
    : rounds * perDay + current;

  const logicalRoundTarget =
    isGoal && activeCounter
      ? Math.max(
          1,
          getCurrentRoundTarget(activeCounter) || perDay || 1
        )
      : Math.max(1, perDay || 1);

  const todayDisplayDen =
    isGoal && activeCounter
      ? Math.max(
          1,
          getGoalRoundDisplayBatchSize(activeCounter) ||
            logicalRoundTarget
        )
      : logicalRoundTarget;

  const targetDone = Boolean(
    activeCounter &&
      !activeCounter.isDisabled &&
      current >= logicalRoundTarget &&
      (!isGoal ||
        (activeCounter.completedCount || 0) <
          (activeCounter.limit || 0))
  );

  // -------------------------
  // Render
  // -------------------------
  return (
    <div className="counterWrapper__footer">
      {/* Progress Summary */}
      <div
        className="roundCounts"
        role="region"
        aria-label="Progress summary"
      >
        <div
          className="roundCounts__ornament"
          aria-hidden="true"
        >
          <span className="roundCounts__ornamentLine" />
          <span className="roundCounts__ornamentGem" />
          <span className="roundCounts__ornamentLine" />
        </div>

        <div className="roundCounts__inner">
          {/* Rounds */}
          <div className="roundCounts__card">
            <span className="roundCounts__eyebrow">
              Rounds
            </span>
            <span
              className="roundCounts__value"
              aria-label={`Rounds: ${rounds}`}
            >
              {rounds}
            </span>
          </div>

          {/* Goal Type */}
          {isGoal ? (
            <>
              {/* Today */}
              <div className="roundCounts__card roundCounts__card--today">
                <span className="roundCounts__eyebrow">
                  Today
                </span>

                <span
                  className="roundCounts__values"
                  aria-label={`This round ${current} of ${todayDisplayDen}`}
                >
                  <span className="roundCounts__num">
                    {current}
                  </span>
                  <span className="roundCounts__sep">/</span>
                  <span className="roundCounts__den">
                    {todayDisplayDen}
                  </span>
                </span>
              </div>

              {/* Total */}
              <div className="roundCounts__card roundCounts__card--total">
                <span className="roundCounts__eyebrow">
                  Total
                </span>

                <span
                  className="roundCounts__values"
                  aria-label={`Total progress ${totalSoFar} of ${limit}`}
                >
                  <span className="roundCounts__num">
                    {totalSoFar}
                  </span>
                  <span className="roundCounts__sep">/</span>
                  <span className="roundCounts__den">
                    {limit}
                  </span>
                </span>
              </div>
            </>
          ) : (
            /* Daily Type */
            <div className="roundCounts__card roundCounts__card--daily">
              <span className="roundCounts__eyebrow">
                Today&apos;s count
              </span>

              <span
                className="roundCounts__values"
                aria-label={`Count ${current} of ${perDay}`}
              >
                <span className="roundCounts__num">
                  {current}
                </span>
                <span className="roundCounts__sep">/</span>
                <span className="roundCounts__den">
                  {perDay}
                </span>
              </span>
            </div>
          )}
        </div>

        {/* Target Achieved */}
        {targetDone && (
          <p className="roundCounts__pill" role="status">
            <span
              className="roundCounts__pillDot"
              aria-hidden
            />
            {isGoal
              ? "Today's target for this batch reached"
              : "Today's target reached"}
          </p>
        )}
      </div>

      {/* Footer Navigation */}
      <div className="counterWrapper__footer-nav">
        {/* Reset */}
        <span
          className={`count-reset-btn ${
            activeCounter?.isDisabled ||
            activeCounter?.currentCount < 1
              ? "disabled"
              : ""
          }`}
          onClick={() => {
            onTapFeedback?.();
            onResetCount();
          }}
        >
          <img alt="" src={resetIcon} title="Reset" />
        </span>

        {/* Decrement */}
        <span
          className={`count-down ${
            !activeCounter ||
            activeCounter.isDisabled ||
            activeCounter.currentCount < 1
              ? "disabled"
              : ""
          }`}
          onClick={() => {
            onTapFeedback?.();
            onDecrement();
          }}
        >
          <img alt="" src={minusIcon} title="Decrement" />
        </span>

        {/* Add Counter */}
        <span
          className="count-up"
          onClick={() => {
            onTapFeedback?.();
            onShowCreateCounter();
          }}
        >
          <img alt="" src={plusIcon} title="Add Counter" />
        </span>

        {/* List */}
        <span
          className="count-lists"
          onClick={() => {
            onTapFeedback?.();
            onShowLists();
          }}
        >
          <img alt="" src={listIcon} title="Lists" />
        </span>

        {/* Settings */}
        <button
          type="button"
          className="setting"
          onClick={() => {
            onTapFeedback?.();
            onOpenSettings();
          }}
          aria-label="Open settings"
        >
          <img alt="" src={dataIcon} title="Settings" />
        </button>
      </div>
    </div>
  );
}